import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import { Save, Loader, Plus, Image, Clock, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const StageProgressForm = ({ projectId, currentStage, onSuccess }) => {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState(0);
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => projectsService.recordStageProgress(projectId, data),
    onSuccess: () => {
      toast.success('Progresso registrado!');
      setDescription('');
      setProgress(0);
      setNotes('');
      queryClient.invalidateQueries(['project-progress', projectId]);
      queryClient.invalidateQueries(['project-stages', projectId]);
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao registrar progresso');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    mutation.mutate({
      description,
      progress_percentage: progress,
      notes
    });
  };

  const STAGE_LABELS = {
    PLANNING: 'Planejamento',
    PURCHASING: 'Compras',
    PRODUCTION: 'Produção',
    ASSEMBLY: 'Montagem',
    COMPLETED: 'Concluído',
    DELIVERED: 'Entregue'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Plus className="w-5 h-5" />
        Registrar Progresso - {STAGE_LABELS[currentStage]}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Progresso */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Porcentagem de Conclusão
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => setProgress(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-lg font-semibold text-primary-600 w-16 text-center">
              {progress}%
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Descrição */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            O que foi feito? *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg"
            rows={3}
            placeholder="Descreva as atividades realizadas nesta etapa..."
            required
          />
        </div>

        {/* Observações */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Observações adicionais
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg"
            rows={2}
            placeholder="Problemas, pendências, observações..."
          />
        </div>

        {/* Botão */}
        <button
          type="submit"
          disabled={mutation.isPending || !description.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {mutation.isPending ? 'Salvando...' : 'Registrar Progresso'}
        </button>
      </form>
    </div>
  );
};

export default StageProgressForm;